import React, { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, Col, Flex, Row, Select } from 'antd';
import { Text } from 'theme/typography';
import { COLORS, TYPOGRAPHY_SIZES } from 'theme';
import { FilterWrapper } from '../SegmentationLabels/styled';
import { AdvancedFiltersOptions, AdvancedFilterValue } from 'models/advancedFilters';
import AdvancedFilters from '../molecules/advancedFilters';
import ActiveFilters from '../molecules/activeFilters';
import { useViewLabelFilterState } from 'hooks/useFilterState';

interface IProps {
  primaryOptions: AdvancedFilterValue[];
  advancedFiltersOptions: AdvancedFiltersOptions;
  handlePrimaryOptChange: (e: AdvancedFilterValue) => void;
  onHandleClear: () => void;
  onHandleApply: () => void;
  clearViewLabels?: () => void;
}

const ViewLabelFilter: FC<IProps> = props => {
  const {
    primaryOptions,
    advancedFiltersOptions,
    handlePrimaryOptChange,
    onHandleClear,
    onHandleApply,
    clearViewLabels,
  } = props;

  const vl = useViewLabelFilterState();
  const { filterState, updateFilterTags } = vl;

  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingRemove, setPendingRemove] = useState<AdvancedFilterValue[]>([]);
  const [pendingApply, setPendingApply] = useState(false);
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(() => ({
    ...filterState.advancedFilters,
    annotationSetName: (filterState.advancedFilters as any).annotationSetName,
  }));
  const [pendingView, setPendingView] = useState(() => filterState.view);

  const handleApply = () => {
    handlePendingRemovals();
    if (pendingView) vl.setView(pendingView);
    else vl.setView(undefined);
    (Object.entries(pendingAdvancedFilters) as [string, any][]).forEach(([key, value]) => {
      vl.setAdvancedFilter(key, value as any);
    });
    setPendingApply(true);
  };

  useEffect(() => {
    setPendingView(filterState.view);
  }, [filterState.view]);

  useEffect(() => {
    setPendingAdvancedFilters(filterState.advancedFilters);
  }, [filterState.advancedFilters]);

  // NOTE: don't auto-apply before we've hydrated pendingAdvancedFilters from
  // the search params. We'll trigger apply after the mapping effect below.

  const updateQueryParams = () => {
    const params: Record<string, string> = {};

    if (filterState.view?.name) {
      params['view'] = filterState.view.name;
    }

    // Handle datasource (single select)
    if (filterState.advancedFilters.datasources && filterState.advancedFilters.datasources.name) {
      params['datasources'] = filterState.advancedFilters.datasources.name;
    }

    // Handle dicomICID (string)
    if (filterState.advancedFilters.dicomICID && filterState.advancedFilters.dicomICID.trim()) {
      params['dicomICID'] = filterState.advancedFilters.dicomICID;
    }

    // Handle study (string)
    if (filterState.advancedFilters.study && filterState.advancedFilters.study.trim()) {
      params['study'] = filterState.advancedFilters.study;
    }

    if (
      filterState.advancedFilters.viewLabelICID &&
      filterState.advancedFilters.viewLabelICID.trim()
    ) {
      params['viewLabelICID'] = filterState.advancedFilters.viewLabelICID;
    }

    if (filterState.advancedFilters.technician && filterState.advancedFilters.technician.trim()) {
      params['technician'] = filterState.advancedFilters.technician;
    }

    if (filterState.advancedFilters.created_at_after) {
      params['created_at_after'] = filterState.advancedFilters.created_at_after;
    }
    if (filterState.advancedFilters.created_at_before) {
      params['created_at_before'] = filterState.advancedFilters.created_at_before;
    }
    if (filterState.advancedFilters.updated_at_after) {
      params['updated_at_after'] = filterState.advancedFilters.updated_at_after;
    }
    if (filterState.advancedFilters.updated_at_before) {
      params['updated_at_before'] = filterState.advancedFilters.updated_at_before;
    }

    // Annotation set name
    if (
      filterState.advancedFilters.annotationSetName &&
      typeof filterState.advancedFilters.annotationSetName === 'string' &&
      filterState.advancedFilters.annotationSetName.trim()
    ) {
      params['annotation_set_name'] = filterState.advancedFilters.annotationSetName;
    }

    setSearchParams(params);
  };

  useEffect(() => {
    if (!primaryOptions.length) return;

    const viewParam = searchParams.get('view');
    if (viewParam) {
      const foundView =
        primaryOptions.find(opt => opt.name === viewParam) ||
        primaryOptions.find(opt => opt.uuid === viewParam);
      if (foundView) {
        vl.setView(foundView);
        setPendingView(foundView);
      } else {
        vl.setView(undefined);
        setPendingView(undefined);
      }
    }
  }, [searchParams, primaryOptions]);

  useEffect(() => {
    // --- Datasource: map name or uuid to object (single-select) ---
    const datasourceParam = searchParams.get('datasources');
    if (datasourceParam && advancedFiltersOptions.datasources) {
      const foundDatasource = (advancedFiltersOptions.datasources as AdvancedFilterValue[]).find(
        opt => opt.name === datasourceParam || opt.uuid === datasourceParam
      );
      vl.setAdvancedFilter('datasources', foundDatasource || undefined);
    } else {
      vl.setAdvancedFilter('datasources', undefined);
    }

    // --- DicomICID: string ---
    const dicomICIDParam = searchParams.get('dicomICID');
    if (dicomICIDParam) {
      vl.setAdvancedFilter('dicomICID', dicomICIDParam);
    } else {
      vl.setAdvancedFilter('dicomICID', '');
    }

    // --- Study: string ---
    const studyParam = searchParams.get('study');
    vl.setAdvancedFilter('study', studyParam || '');

    const viewLabelICIDParam = searchParams.get('viewLabelICID');
    if (viewLabelICIDParam) {
      vl.setAdvancedFilter('viewLabelICID', viewLabelICIDParam);
    } else {
      vl.setAdvancedFilter('viewLabelICID', '');
    }

    const technicianParam = searchParams.get('technician');
    if (technicianParam) {
      vl.setAdvancedFilter('technician', technicianParam);
    } else {
      vl.setAdvancedFilter('technician', undefined);
    }

    const createdAtAfterParam = searchParams.get('created_at_after');
    vl.setAdvancedFilter('created_at_after', createdAtAfterParam || undefined);

    const createdAtBeforeParam = searchParams.get('created_at_before');
    vl.setAdvancedFilter('created_at_before', createdAtBeforeParam || undefined);

    const updatedAtAfterParam = searchParams.get('updated_at_after');
    vl.setAdvancedFilter('updated_at_after', updatedAtAfterParam || undefined);

    const updatedAtBeforeParam = searchParams.get('updated_at_before');
    vl.setAdvancedFilter('updated_at_before', updatedAtBeforeParam || undefined);

    // --- Annotation set name (support full and short param names) ---
    const annotationSetParam = searchParams.get('annotation_set_name');
    if (annotationSetParam) {
      vl.setAdvancedFilter('annotationSetName', annotationSetParam || '');
    } else {
      vl.setAdvancedFilter('annotationSetName', '');
    }

    // Backwards-compatible short alias
    const annotationSetShort = searchParams.get('annotation_set');
    if (annotationSetShort) {
      vl.setAdvancedFilter('annotationSetName', annotationSetShort || '');
    }
  }, [searchParams, primaryOptions, advancedFiltersOptions]);

  useEffect(() => {
    // Trigger tag updates if there are active filters or any query params
    const hasActiveFilters =
      filterState.view ||
      Object.values(filterState.advancedFilters).some(
        val =>
          (Array.isArray(val) && val.length > 0) ||
          (typeof val === 'object' && val !== null && 'name' in val) ||
          (typeof val === 'string' && val.trim() !== '')
      );

    if (hasActiveFilters || Array.from(searchParams.keys()).length > 0) {
      updateFilterTags();
    }
  }, [filterState.view, filterState.advancedFilters, searchParams, updateFilterTags]);

  const handlePrimaryChange = (value: string, option: any) => {
    // value should be the selected option's uuid (per fieldNames.value = 'uuid')
    const found = primaryOptions.find(opt => opt.uuid === value || opt.name === value);
    if (found) {
      setPendingView({ uuid: found.uuid, name: found.name });
      handlePrimaryOptChange({ uuid: found.uuid, name: found.name });
    } else {
      // fallback when option object is provided or value is the name
      const uuid = option && (option.uuid || option.value) ? option.uuid || option.value : value;
      const name = option && option.name ? option.name : String(value);
      setPendingView({ uuid, name });
      handlePrimaryOptChange({ uuid, name });
    }
  };

  const handleAdvancedFiltersChange = (
    key: string,
    value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
  ) => {
    setPendingAdvancedFilters((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    vl.resetFilters();
    onHandleClear();
    setSearchParams({});
    setPendingView(undefined);
    setPendingAdvancedFilters({
      datasources: undefined,
      dicomICID: undefined,
      study: undefined,
      viewLabelICID: undefined,
      annotationSetName: undefined,
      technician: undefined,
      created_at_after: undefined,
      created_at_before: undefined,
      updated_at_after: undefined,
      updated_at_before: undefined,
    });
    if (clearViewLabels) clearViewLabels();
  };

  const handlePendingRemovals = () => {
    pendingRemove.forEach(tag => {
      // Remove from the actual Redux filter state
      vl.removeTag(tag, false);

      // Also clear any pending selections so Apply won't reintroduce them
      const tagName = tag.name || '';
      const [rawKey] = tagName.split(':');
      const key = rawKey?.trim();

      if (key?.toLowerCase() === 'view') {
        setPendingView(undefined);
      } else if (key) {
        setPendingAdvancedFilters((prev: any) => {
          const next = { ...prev } as any;
          const currentVal = (next as any)[key];

          if (Array.isArray(currentVal)) {
            (next as any)[key] = currentVal.filter((item: any) => item?.uuid !== tag.uuid);
          } else if (currentVal && typeof currentVal === 'object') {
            if (currentVal.uuid === tag.uuid) {
              (next as any)[key] = undefined;
            }
          } else if (typeof currentVal === 'string') {
            (next as any)[key] = undefined;
          }
          return next;
        });
      }
    });
    setPendingRemove([]);
  };

  const isObjectFilterEqual = (a: any, b: any) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.uuid === b.uuid && a.name === b.name;
  };

  const areAdvancedFiltersEqual = (a: any, b: any) => {
    const keys = [
      'datasources',
      'dicomICID',
      'study',
      'viewLabelICID',
      'annotationSetName',
      'technician',
      'created_at_after',
      'created_at_before',
      'updated_at_after',
      'updated_at_before',
    ];
    for (const key of keys) {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        if ((av || '') !== (bv || '')) return false;
      } else {
        if (!isObjectFilterEqual(av, bv)) return false;
      }
    }
    return true;
  };

  const isFilterStateSyncedWithPending = () => {
    const syncedView = isObjectFilterEqual(filterState.view, pendingView);
    const syncedAdv = areAdvancedFiltersEqual(filterState.advancedFilters, pendingAdvancedFilters);
    return syncedView && syncedAdv;
  };

  useEffect(() => {
    if (!pendingApply) return;
    if (!isFilterStateSyncedWithPending()) return;
    updateQueryParams();
    updateFilterTags();
    onHandleApply();
    setPendingApply(false);
  }, [filterState, pendingApply]);

  return (
    <FilterWrapper>
      <Card>
        <Flex vertical gap="middle">
          <Row gutter={[16, 16]} style={{ width: '100%' }} align="bottom">
            <Col xs={24} md={12} xl={6}>
              <Flex vertical>
                <Text size={TYPOGRAPHY_SIZES.NORMAL} color={COLORS.darkBlue} weight={600}>
                  View
                </Text>
                <Flex style={{ gap: 8 }}>
                  <Select
                    style={{ flex: 1 }}
                    showSearch
                    placeholder="Choose a view"
                    optionFilterProp="label"
                    onChange={handlePrimaryChange}
                    fieldNames={{
                      label: 'name',
                      value: 'uuid',
                    }}
                    filterOption={(input, option) =>
                      (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={primaryOptions}
                    value={pendingView?.uuid}
                  />
                </Flex>
              </Flex>
            </Col>
            <Col xs={24} md={24} xl={18}>
              <AdvancedFilters
                advancedFiltersOptions={advancedFiltersOptions}
                handleAdvancedFiltersOptChange={handleAdvancedFiltersChange}
                advancedFilters={pendingAdvancedFilters as any}
                isViewLabelMode={true}
              />
            </Col>
          </Row>

          <ActiveFilters
            pendingRemove={pendingRemove}
            setPendingRemove={setPendingRemove}
            viewLabelMode={true}
            onTagPendingRemove={(tag: AdvancedFilterValue) => {
              const tagName = tag.name || '';
              const [rawKey] = tagName.split(':');
              const key = rawKey?.trim();
              if (key?.toLowerCase() === 'view') {
                setPendingView(undefined);
              } else if (key) {
                setPendingAdvancedFilters((prev: any) => {
                  const next = { ...prev } as any;
                  const currentVal = (next as any)[key];

                  if (Array.isArray(currentVal)) {
                    (next as any)[key] = currentVal.filter((item: any) => item?.uuid !== tag.uuid);
                  } else if (currentVal && typeof currentVal === 'object') {
                    if (currentVal.uuid === tag.uuid) {
                      (next as any)[key] = undefined;
                    }
                  } else if (typeof currentVal === 'string') {
                    (next as any)[key] = undefined;
                  }
                  return next;
                });
              }
              // Also clear corresponding query param from URL immediately
              try {
                const paramMap: { [k: string]: string } = {
                  view: 'view',
                  datasources: 'datasources',
                  dicomicid: 'dicomICID',
                  study: 'study',
                  viewlabelicid: 'viewLabelICID',
                  technician: 'technician',
                  annotationSetName: 'annotation_set_name',
                  created_at_after: 'created_at_after',
                  created_at_before: 'created_at_before',
                  updated_at_after: 'updated_at_after',
                  updated_at_before: 'updated_at_before',
                };

                const rawKeyLower = (rawKey || '').trim().toLowerCase();
                const paramName = paramMap[rawKeyLower];
                if (paramName) {
                  const val = tagName.split(':').slice(1).join(':').trim();
                  const newParams = new URLSearchParams(searchParams);
                  const existing = newParams.get(paramName);
                  if (existing) {
                    const parts = existing
                      .split(',')
                      .map(p => decodeURIComponent(p).trim())
                      .filter(Boolean);
                    const filtered = parts.filter(p => p !== val);
                    if (filtered.length === 0) {
                      newParams.delete(paramName);
                    } else {
                      newParams.set(paramName, filtered.join(','));
                    }
                  }
                  setSearchParams(newParams);
                }
              } catch (err) {
                // ignore URL update failures
              }
            }}
          />

          <Flex gap={8} justify="end">
            <Button onClick={handleClear}>Clear</Button>
            <Button onClick={handleApply} type="primary">
              Apply Filters
            </Button>
          </Flex>
        </Flex>
      </Card>
    </FilterWrapper>
  );
};

export default ViewLabelFilter;
